import React, { useState, useEffect, useRef } from 'react';
import { getNonce, getConfig, adminAjax } from './Helpers';
// import './frontend-widget.scss';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInWithPopup, 
    GoogleAuthProvider, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    updateProfile
} from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    addDoc, 
    query, 
    orderBy, 
    limit, 
    onSnapshot,
    serverTimestamp,
    where,
    doc,
    updateDoc,
    setDoc,
    getDocs,
    deleteDoc
} from 'firebase/firestore';
import { 
    getStorage, 
    ref, 
    uploadBytes, 
    getDownloadURL 
} from 'firebase/storage';

// Add this component to handle image loading issues
const SafeImage = ({ src, alt, className, fallbackSrc }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        // Check if it's a Google user content URL that needs proxying
        if (src && src.includes('googleusercontent.com')) {
            const proxyUrl = `${adminAjax()}?action=acb_proxy_image&url=${encodeURIComponent(src)}&nonce=${getNonce()}`;
            setImgSrc(proxyUrl);
        } else {
            setImgSrc(src);
        }
        setHasError(false);
    }, [src]);

    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            // Try the original URL first if proxy failed
            if (imgSrc.includes('admin-ajax.php')) {
                setImgSrc(src);
            } else {
                // If original also failed, use fallback
                setImgSrc(fallbackSrc || 'https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg');
            }
        }
    };

    const handleLoad = () => {
        setHasError(false);
    };

    return (
        <img 
            src={imgSrc}
            alt={alt}
            className={className}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={handleError}
            onLoad={handleLoad}
            loading="lazy"
        />
    );
}

const FrontendWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [user, setUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [showLogin, setShowLogin] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [signUpDisplayName, setSignUpDisplayName] = useState('');
    const [signUpProfileImage, setSignUpProfileImage] = useState(null);
    const [signUpProfileImagePreview, setSignUpProfileImagePreview] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [firebaseError, setFirebaseError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState({});
    const [lastMessageTimes, setLastMessageTimes] = useState({});
    const [unreadCount, setUnreadCount] = useState(0);
    const [supportSystemEnabled, setSupportSystemEnabled] = useState(false);
    const [currentUserIsAdmin, setCurrentUserIsAdmin] = useState(false);
    const [widgetSettings, setWidgetSettings] = useState({
        frontend_widget_enabled: true,
        widget_position: 'bottom-right',
        widget_title: 'Chat Support',
        widget_description: '',
        widget_primary_color: '#5A5EB9'
    });
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const profileImageInputRef = useRef(null);
    const [firebaseConfig, setFirebaseConfig] = useState(null);
    const [app, setApp] = useState(null);
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [storage, setStorage] = useState(null);

    useEffect(() => {
        // Get Firebase config from WordPress
        const config = getConfig();
        
        if (config && config.apiKey) {
            setFirebaseConfig(config);
            try {
                const firebaseApp = initializeApp(config);
                const firebaseAuth = getAuth(firebaseApp);
                const firestoreDb = getFirestore(firebaseApp);
                const firebaseStorage = getStorage(firebaseApp);
                
                setApp(firebaseApp);
                setAuth(firebaseAuth);
                setDb(firestoreDb);
                setStorage(firebaseStorage);
                
                // Listen for auth state changes
                const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
                    setUser(user);
                    setLoading(false);
                    if (user) {
                        // console.log('User signed in:', user.email);
                        updateUserStatus(user, true);
                        authenticateWithWordPress(user);
                    }
                });
                
                return () => unsubscribe();
            } catch (error) {
                console.error('Firebase initialization error:', error);
                setFirebaseError('Failed to initialize Firebase: ' + error.message);
                setLoading(false);
            }
        } else {
            setFirebaseError('Firebase configuration not found.');
            setLoading(false);
        }
    }, []);

    // Load support system settings and current user admin status
    useEffect(() => {
        if (user) {
            loadSupportSystemSettings();
            checkCurrentUserAdminStatus();
        }
        // Load widget settings on component mount
        loadWidgetSettings();
    }, [user]);

    // Refresh support system settings periodically
    useEffect(() => {
        if (user) {
            const refreshInterval = setInterval(() => {
                loadSupportSystemSettings();
                checkCurrentUserAdminStatus();
            }, 30000); // Refresh every 30 seconds
            
            return () => clearInterval(refreshInterval);
        }
    }, [user]);

    // Listen for users
    useEffect(() => {
        if (user && db) {
            const usersQuery = query(
                collection(db, 'users'),
                where('uid', '!=', user.uid)
            );
            
            const unsubscribe = onSnapshot(usersQuery, async (querySnapshot) => {
                const userList = [];
                querySnapshot.forEach((doc) => {
                    const userData = { id: doc.id, ...doc.data() };
                    if (!userData.uid.startsWith('test_user_')) {
                        userList.push(userData);
                    }
                });
                
                // Fetch admin status for all users from WordPress database
                if (userList.length > 0) {
                    try {
                        const usersWithAdminStatus = await fetchUsersWithAdminStatus(userList);

                        setUsers(usersWithAdminStatus);
                    } catch (error) {
                        console.error('Frontend widget - Error fetching admin status:', error);
                        setUsers(userList); // Fallback to users without admin status
                    }
                } else {
                    setUsers([]);
                }
            });
            
            return () => unsubscribe();
        }
    }, [user, db]);

    // Listen for unread messages
    useEffect(() => {
        if (user && db) {
            const messagesQuery = query(
                collection(db, 'chats'),
                orderBy('lastMessageTime', 'desc')
            );
            
            const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
                let totalUnread = 0;
                querySnapshot.forEach((chatDoc) => {
                    const chatId = chatDoc.id;
                    const chatData = chatDoc.data();
                    
                    if (chatId.includes(user.uid)) {
                        const otherUserId = chatId.split('_').find(uid => uid !== user.uid);
                        
                        if (otherUserId && chatData.lastMessage && chatData.lastMessage.uid !== user.uid) {
                            const count = unreadMessages[otherUserId] || 0;
                            totalUnread += count;
                        }
                    }
                });
                setUnreadCount(totalUnread);
            });
            
            return () => unsubscribe();
        }
    }, [user, db, unreadMessages]);

    // Listen for messages when a user is selected
    useEffect(() => {
        if (user && selectedUser && db) {
            const chatId = getChatId(user.uid, selectedUser.uid);
            const messagesQuery = query(
                collection(db, 'chats', chatId, 'messages'),
                orderBy('timestamp', 'asc')
            );
            
            const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
                const messageList = [];
                querySnapshot.forEach((doc) => {
                    messageList.push({ id: doc.id, ...doc.data() });
                });
                setMessages(messageList);
                
                // Clear unread messages for this user
                if (selectedUser.uid) {
                    setUnreadMessages(prev => {
                        const newUnread = { ...prev };
                        delete newUnread[selectedUser.uid];
                        return newUnread;
                    });
                }
            });
            
            return () => unsubscribe();
        }
    }, [user, selectedUser, db]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const updateUserStatus = async (user, isOnline) => {
        if (!db) return;
        
        try {
            await setDoc(doc(db, 'users', user.uid), {
                uid: user.uid,
                displayName: user.displayName || user.email,
                email: user.email,
                photoURL: user.photoURL || '',
                isOnline: isOnline,
                lastSeen: serverTimestamp(),
                createdAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const getChatId = (uid1, uid2) => {
        return [uid1, uid2].sort().join('_');
    };

    const authenticateWithWordPress = async (firebaseUser) => {
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_firebase_auth',
                    nonce: getNonce(),
                    firebase_uid: firebaseUser.uid,
                    user_name: firebaseUser.displayName || firebaseUser.email,
                    user_email: firebaseUser.email,
                    user_avatar: firebaseUser.photoURL || '',
                    auth_provider: firebaseUser.providerData[0]?.providerId || 'email'
                })
            });
            
            const data = await response.json();
            if (!data.success) {
                console.error('WordPress authentication failed:', data.data);
            }
        } catch (error) {
            console.error('Error authenticating with WordPress:', error);
        }
    };

    const signInWithGoogle = async () => {
        if (!auth) return;
        
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account'
        });
        
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error('Google sign-in error:', error);
            setFirebaseError('Google sign-in failed: ' + error.message);
        }
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSignUpProfileImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setSignUpProfileImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadProfileImage = async (file) => {
        if (!storage || !file) return null;
        
        try {
            const storageRef = ref(storage, `profile-images/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading profile image:', error);
            return null;
        }
    };

    const signInWithEmail = async () => {
        if (!auth) return;
        
        try {
            if (isSignUp) {
                const userCredential = await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
                const newUser = userCredential.user;
                
                let photoURL = '';
                if (signUpProfileImage) {
                    photoURL = await uploadProfileImage(signUpProfileImage);
                }
                
                await updateProfile(newUser, {
                    displayName: signUpDisplayName || loginEmail,
                    photoURL: photoURL
                });
            } else {
                await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
            }
            
            setShowLogin(false);
            setLoginEmail('');
            setLoginPassword('');
            setSignUpDisplayName('');
            setSignUpProfileImage(null);
            setSignUpProfileImagePreview('');
        } catch (error) {
            console.error('Email sign-in error:', error);
            setFirebaseError('Authentication failed: ' + error.message);
        }
    };

    const handleSignOut = async () => {
        if (!auth) return;
        
        try {
            if (db && user) {
                await updateUserStatus(user, false);
            }
            
            setUser(null);
            setSelectedUser(null);
            setMessages([]);
            setUsers([]);
            setUnreadMessages({});
            setLastMessageTimes({});
            
            await signOut(auth);
        } catch (error) {
            console.error('Sign-out error:', error);
        }
    };

    const sendMessage = async (text, type = 'text', fileUrl = null, fileName = null) => {
        if (!user || !selectedUser || !db || (!text.trim() && !fileUrl)) return;
        
        try {
            const chatId = getChatId(user.uid, selectedUser.uid);
            const messageData = {
                text: text,
                uid: user.uid,
                displayName: user.displayName || user.email,
                email: user.email,
                photoURL: user.photoURL || '',
                timestamp: serverTimestamp(),
                type: type
            };

            if (fileUrl) {
                messageData.fileUrl = fileUrl;
                messageData.fileName = fileName;
            }

            await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
            
            await setDoc(doc(db, 'chats', chatId), {
                lastMessage: messageData,
                lastMessageTime: serverTimestamp(),
                participants: [user.uid, selectedUser.uid]
            }, { merge: true });
            
            if (type === 'text') {
                setNewMessage('');
            }
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleFileUpload = async (file) => {
        if (!storage || !file) return;
        
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            setFirebaseError('File too large. Maximum size is 10MB.');
            return;
        }
        
        setUploading(true);
        try {
            const storageRef = ref(storage, `chat-files/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            const fileType = file.type.startsWith('image/') ? 'image' : 'file';
            await sendMessage('', fileType, downloadURL, file.name);
        } catch (error) {
            console.error('Error uploading file:', error);
            setFirebaseError('File upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    const filteredUsers = users.filter(user => {
        // First apply support system filtering
        let shouldShow = true;

        if (supportSystemEnabled) {
            if (currentUserIsAdmin) {
                shouldShow = true;
            } else {
                shouldShow = user.isAdmin === true;
            }
        }
        // If support system is disabled, show all users (shouldShow remains true)

        if (!shouldShow) {
            return false;
        }
        
        // Then apply search filtering
        if (!searchTerm.trim()) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const displayName = user.displayName?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';
        
        return displayName.includes(searchLower) || email.includes(searchLower);
    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getUnreadCount = (userId) => {
        return unreadMessages[userId] || 0;
    };

    const hasNewMessage = (userId) => {
        return getUnreadCount(userId) > 0;
    };

    // Load support system settings
    const loadSupportSystemSettings = async () => {
        try {
            const data = await makeAjaxRequest('acb_get_support_settings');
            if (data.success) {
                setSupportSystemEnabled(data.data.support_system_enabled);
            } else {
                console.error('Frontend widget - Failed to load support settings:', data.data);
            }
        } catch (error) {
            console.error('Frontend widget - Error loading support settings:', error);
        }
    };

    // Check if current user is admin
    const checkCurrentUserAdminStatus = async () => {
        if (!user) return;
        
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_check_user_admin_status',
                    nonce: getNonce(),
                    firebase_uid: user.uid
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setCurrentUserIsAdmin(data.data.is_admin);
                console.log('Frontend widget - Current user is admin:', data.data.is_admin);
            } else {
                console.error('Frontend widget - Failed to check admin status:', data.data);
                setCurrentUserIsAdmin(false);
            }
        } catch (error) {
            console.error('Frontend widget - Error checking admin status:', error);
            setCurrentUserIsAdmin(false);
        }
    };

    // Load widget settings
    const loadWidgetSettings = async () => {
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_get_widget_settings',
                    nonce: getNonce()
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setWidgetSettings(data.data.widget_settings);
                console.log('Frontend widget - Widget settings loaded:', data.data.widget_settings);
            } else {
                console.error('Frontend widget - Failed to load widget settings:', data.data);
            }
        } catch (error) {
            console.error('Frontend widget - Error loading widget settings:', error);
        }
    };

    // Fetch users with admin status from WordPress database
    const fetchUsersWithAdminStatus = async (firestoreUsers) => {
        try {
            console.log('Frontend widget - Fetching admin status for users:', firestoreUsers.map(u => u.email));
            
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_get_users',
                    nonce: getNonce(),
                    current_user_uid: user?.uid || ''
                })
            });
            
            const data = await response.json();
            console.log('Frontend widget - WordPress users response:', data);
            
            if (data.success) {
                const wpUsers = data.data.users;
                console.log('Frontend widget - WordPress users found:', wpUsers.length);
                console.log('Frontend widget - WordPress users:', wpUsers.map(u => ({ email: u.user_email, firebase_uid: u.firebase_uid, is_admin: u.is_admin })));
                
                // Merge Firestore users with WordPress admin status
                const mergedUsers = firestoreUsers.map(firestoreUser => {
                    const wpUser = wpUsers.find(wp => wp.firebase_uid === firestoreUser.uid);
                    const isAdmin = wpUser ? Boolean(wpUser.is_admin) : false;
                    
                    console.log('Frontend widget - Merging user:', firestoreUser.email, {
                        firestoreUID: firestoreUser.uid,
                        wpUserFound: !!wpUser,
                        wpUserIsAdmin: wpUser ? wpUser.is_admin : 'N/A',
                        finalIsAdmin: isAdmin
                    });
                    
                    return {
                        ...firestoreUser,
                        isAdmin: isAdmin,
                        wpUserId: wpUser ? wpUser.id : null
                    };
                });
                
                console.log('Frontend widget - Merged users with admin status:', mergedUsers);
                return mergedUsers;
            } else {
                console.error('Frontend widget - Failed to fetch users from WordPress:', data.data);
                // Return users with isAdmin set to false as fallback
                return firestoreUsers.map(user => ({ ...user, isAdmin: false }));
            }
        } catch (error) {
            console.error('Frontend widget - Error fetching users with admin status:', error);
            // Return users with isAdmin set to false as fallback
            return firestoreUsers.map(user => ({ ...user, isAdmin: false }));
        }
    };

    if (loading) {
        return null;
    }

    if (firebaseError) {
        return null;
    }

    // Don't render widget if it's disabled in settings
    if (!widgetSettings.frontend_widget_enabled) {
        return null;
    }

    // Get position class and styles
    const getPositionClass = () => {
        return `acb-widget-position-${widgetSettings.widget_position}`;
    };

    const getPositionStyles = () => {
        const position = widgetSettings.widget_position;
        const styles = {
            '--acb-primary-color': widgetSettings.widget_primary_color
        };

        switch (position) {
            case 'bottom-right':
                return { ...styles, position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 };
            case 'bottom-left':
                return { ...styles, position: 'fixed', bottom: '20px', left: '20px', zIndex: 9999 };
            case 'top-right':
                return { ...styles, position: 'fixed', top: '20px', right: '20px', zIndex: 9999 };
            case 'top-left':
                return { ...styles, position: 'fixed', top: '20px', left: '20px', zIndex: 9999 };
            case 'inline':
                return { ...styles, position: 'relative', display: 'block', margin: '20px 0' };
            default:
                return { ...styles, position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 };
        }
    };

    console.log(widgetSettings.widget_description)

    return (
        <div className={`acb-widget-container ${getPositionClass()}`} style={getPositionStyles()}>
            {/* Call-to-Action Description */}
            {!isOpen && widgetSettings.widget_description && widgetSettings.widget_description.trim() && (
                <div className="acb-widget-cta" style={{ '--acb-primary-color': widgetSettings.widget_primary_color }}>
                    <div className="acb-widget-cta-content">
                        <span className="acb-widget-cta-text">{widgetSettings.widget_description}</span>
                        <div className="acb-widget-cta-wave">👋</div>
                    </div>
                </div>
            )}
            
            {/* Floating Chat Button */}
            <div 
                className={`acb-widget-button ${isOpen ? 'hidden' : ''}`}
                onClick={() => setIsOpen(true)}
                style={{ backgroundColor: widgetSettings.widget_primary_color }}
            >
                <i className="fas fa-comments"></i>
                {unreadCount > 0 && (
                    <span className="acb-widget-badge">{unreadCount}</span>
                )}
            </div>

            {/* Chat Widget */}
            {isOpen && (
                <div className={`acb-widget ${isMinimized ? 'minimized' : ''}`}>
                    {/* Widget Header */}
                    <div className="acb-widget-header" style={{ backgroundColor: widgetSettings.widget_primary_color }}>
                        <div className="acb-widget-title">
                            <i className="fas fa-comments"></i>
                            <span>{widgetSettings.widget_title}</span>
                        </div>
                        <div className="acb-widget-controls">
                            <button 
                                className="acb-widget-minimize"
                                onClick={() => setIsMinimized(!isMinimized)}
                            >
                                <i className={`fas fa-${isMinimized ? 'expand' : 'minus'}`}></i>
                            </button>
                            <button 
                                className="acb-widget-close"
                                onClick={() => setIsOpen(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {!isMinimized && (
                        <div className="acb-widget-content">
                            {!user ? (
                                // Login/Register Form
                                <div className="acb-widget-auth">
                                    <h3>{widgetSettings.widget_title}</h3>
                                    {widgetSettings.widget_description && widgetSettings.widget_description.trim() && (
                                        <p>{widgetSettings.widget_description}</p>
                                    )}
                                    
                                    <button 
                                        className="acb-widget-google-btn"
                                        onClick={signInWithGoogle}
                                    >
                                        <i className="fab fa-google"></i>
                                        Sign in with Google
                                    </button>
                                    
                                    <div className="acb-widget-divider">
                                        <span>or</span>
                                    </div>
                                    
                                    <button 
                                        className="acb-widget-email-btn"
                                        onClick={() => setShowLogin(true)}
                                    >
                                        Sign in with Email
                                    </button>
                                    
                                    {showLogin && (
                                        <div className="acb-widget-email-form">
                                            {isSignUp && (
                                                <>
                                                    <input
                                                        type="text"
                                                        placeholder="Display Name"
                                                        value={signUpDisplayName}
                                                        onChange={(e) => setSignUpDisplayName(e.target.value)}
                                                    />
                                                    <div className="acb-widget-profile-section">
                                                        <button 
                                                            type="button"
                                                            className="acb-widget-upload-btn"
                                                            onClick={() => profileImageInputRef.current?.click()}
                                                        >
                                                            {signUpProfileImagePreview ? (
                                                                <img 
                                                                    src={signUpProfileImagePreview} 
                                                                    alt="Profile preview" 
                                                                    className="acb-widget-profile-preview"
                                                                />
                                                            ) : (
                                                                <i className="fas fa-camera"></i>
                                                            )}
                                                            {signUpProfileImagePreview ? 'Change Photo' : 'Upload Photo'}
                                                        </button>
                                                        <input
                                                            ref={profileImageInputRef}
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleProfileImageChange}
                                                            style={{ display: 'none' }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            <input
                                                type="email"
                                                placeholder="Email"
                                                value={loginEmail}
                                                onChange={(e) => setLoginEmail(e.target.value)}
                                            />
                                            <input
                                                type="password"
                                                placeholder="Password"
                                                value={loginPassword}
                                                onChange={(e) => setLoginPassword(e.target.value)}
                                            />
                                            <div className="acb-widget-form-actions">
                                                <button onClick={signInWithEmail}>
                                                    {isSignUp ? 'Sign Up' : 'Sign In'}
                                                </button>
                                                <button 
                                                    type="button" 
                                                    onClick={() => {
                                                        setIsSignUp(!isSignUp);
                                                        setSignUpDisplayName('');
                                                        setSignUpProfileImage(null);
                                                        setSignUpProfileImagePreview('');
                                                    }}
                                                >
                                                    {isSignUp ? 'Already have account?' : 'Create account'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Chat Interface
                                <div className="acb-widget-chat">
                                    {/* User List */}
                                    <div className="acb-widget-users">
                                        <div className="acb-widget-user-header">
                                            <div className="acb-widget-current-user">
                                                <SafeImage 
                                                    src={user.photoURL} 
                                                    alt={user.displayName || user.email}  
                                                    className="acb-widget-user-avatar"
                                                    fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                                />
                                                <span>{user.displayName || user.email}</span>
                                            </div>
                                            <button onClick={handleSignOut} className="acb-widget-signout">
                                                <i className="fas fa-sign-out-alt"></i>
                                            </button>
                                        </div>
                                        
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="acb-widget-search"
                                        />
                                        
                                        <div className="acb-widget-users-list">
                                            {filteredUsers.map((userItem) => (
                                                <div
                                                    key={userItem.uid}
                                                    className={`acb-widget-user-item ${selectedUser?.uid === userItem.uid ? 'active' : ''} ${hasNewMessage(userItem.uid) ? 'has-new-message' : ''}`}
                                                    onClick={() => setSelectedUser(userItem)}
                                                >
                                          
                                                    <SafeImage 
                                                        src={userItem.photoURL}
                                                        alt={userItem.displayName || userItem.email}
                                                        className="acb-widget-user-avatar"
                                                        fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                                    />
                                                    <div className="acb-widget-user-info">
                                                        <span className="acb-widget-user-name">
                                                            {userItem.displayName || userItem.email}
                                                        </span>
                                                        <span className={`acb-widget-user-status ${userItem.isOnline ? 'online' : 'offline'}`}>
                                                            {userItem.isOnline ? 'Online' : 'Offline'}
                                                        </span>
                                                    </div>
                                                    {hasNewMessage(userItem.uid) && (
                                                        <span className="acb-widget-new-indicator">
                                                            {getUnreadCount(userItem.uid)}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Chat Area */}
                                    <div className="acb-widget-chat-area">
                                        {selectedUser ? (
                                            <>
                                                <div className="acb-widget-chat-header">
                                                    {/* <img 
                                                        src={selectedUser.photoURL || 'https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg'} 
                                                        alt={selectedUser.displayName || selectedUser.email}
                                                        className="acb-widget-user-avatar"
                                                    /> */}
                                                    <SafeImage 
                                                        src={selectedUser.photoURL} 
                                                        alt={selectedUser.displayName || selectedUser.email}  
                                                        className="acb-widget-user-avatar"
                                                        fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                                    />
                                                    <span>{selectedUser.displayName || selectedUser.email}</span>
                                                </div>

                                                <div className="acb-widget-messages">
                                                    {messages.map((message) => {
                                                        const isOwn = message.uid === user.uid;
                                                        return (
                                                            <div key={message.id} className={`acb-widget-message ${isOwn ? 'own' : 'other'}`}>
                                                                <div className="acb-widget-message-content">
                                                                    {message.type === 'image' ? (
                                                                        <img 
                                                                            src={message.fileUrl} 
                                                                            alt="Shared image" 
                                                                            className="acb-widget-message-image"
                                                                        />
                                                                    ) : message.type === 'file' ? (
                                                                        <div className="acb-widget-message-file">
                                                                            <i className="fas fa-file"></i>
                                                                            <a href={message.fileUrl} target="_blank" rel="noopener noreferrer">
                                                                                {message.fileName}
                                                                            </a>
                                                                        </div>
                                                                    ) : (
                                                                        <p>{message.text}</p>
                                                                    )}
                                                                </div>
                                                                <span className="acb-widget-message-time">
                                                                    {formatTime(message.timestamp)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                    <div ref={messagesEndRef} />
                                                </div>

                                                <div className="acb-widget-input">
                                                    <button 
                                                        className="acb-widget-attach"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={uploading}
                                                    >
                                                        <i className="fas fa-paperclip"></i>
                                                    </button>
                                                    <input
                                                        type="text"
                                                        placeholder="Type a message..."
                                                        value={newMessage}
                                                        onChange={(e) => setNewMessage(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && sendMessage(newMessage)}
                                                        disabled={uploading}
                                                    />
                                                    <button 
                                                        onClick={() => sendMessage(newMessage)}
                                                        disabled={!newMessage.trim() && !uploading}
                                                    >
                                                        {uploading ? (
                                                            <i className="fas fa-spinner fa-spin"></i>
                                                        ) : (
                                                            <i className="fas fa-paper-plane"></i>
                                                        )}
                                                    </button>
                                                    
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                handleFileUpload(file);
                                                            }
                                                            e.target.value = '';
                                                        }}
                                                        style={{ display: 'none' }}
                                                        accept="image/*,.pdf,.doc,.docx,.txt"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="acb-widget-no-chat">
                                                <i className="fas fa-comments"></i>
                                                <p>Select a user to start chatting</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FrontendWidget; 